"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    Avancer,
    Curseur,
    Ecran,
    Question,
    Recommencer,
    Resultat,
    euros,
    pourcent,
    remplir,
    useMontants,
    useParcours,
} from "./Journey";
import {
    comparerVersementLiberatoire,
    eligible,
    joursAvantEcheance,
    parts,
    ACTIVITES,
    ANNEE_OPTION_PAR_DEFAUT,
} from "@/lib/tools/incomeTax";
import { PLAFONDS } from "@/lib/tools/microEnterprise";
import { expliquerVersementLiberatoire } from "@/lib/tools/explanations";
import { IndiceExplication, MontantExplicable, PanneauExplication, useExplications } from "./Explanation";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * Barème ou versement libératoire : lequel vous fait payer le moins ?
 *
 * Tout le calcul vit dans lib/tools/incomeTax.js, testé par
 * npm run check:income-tax : ce fichier ne fait que poser des questions, une par
 * écran, et montrer le résultat. La séparation est volontaire, c'est
 * l'exactitude du calcul qui engage, pas la mise en page.
 *
 * DEUX PIÈGES, ET LE RÉSULTAT LES NOMME TOUS LES DEUX.
 *
 * Le premier est le foyer non imposable : au barème il ne paie rien, alors que
 * l'option prélève son forfait dès le premier euro encaissé. Celui qui opte
 * « pour la simplicité » paie un impôt qu'il ne devait pas, et le résultat le
 * lui dit en toutes lettres plutôt que d'afficher un simple perdant.
 *
 * Le second est la règle du taux effectif (article 197 C du CGI) : avec
 * l'option, le chiffre d'affaires sort de la base imposable mais reste compté
 * pour fixer le taux appliqué aux autres revenus du foyer. Les simulateurs
 * grand public l'ignorent et sous-estiment l'impôt d'un foyer mixte de
 * plusieurs milliers d'euros. Quand le lecteur a d'autres revenus, le résultat
 * montre l'écart entre le calcul naïf et le vrai, et dit si ce calcul naïf
 * l'aurait fait opter à tort.
 *
 * LE RÉSULTAT PARLE À LA SITUATION DU LECTEUR. Qui n'a pas encore créé opte à
 * la déclaration de début d'activité ou dans les trois mois qui suivent ; qui
 * est au barème a jusqu'au 30 septembre pour l'année suivante ; qui a déjà
 * opté doit renoncer avant la même date si le barème gagne. Trois situations,
 * trois marches à suivre, sinon le compte à rebours du 30 septembre s'adresse
 * à des gens qu'il ne concerne pas.
 *
 * L'éligibilité (plafond de revenu fiscal de référence, lecture de l'avis
 * d'impôt) a son propre outil, vers lequel le résultat renvoie. Mais quand le
 * revenu simulé dépasse manifestement le plafond, le résultat le dit sous le
 * verdict : ce sont précisément les foyers pour qui l'option gagne le plus qui
 * risquent de ne pas y avoir droit.
 *
 * Les libellés sont embarqués plutôt que tirés de next-intl : le layout racine
 * ne passe qu'un sous-ensemble des messages aux composants clients du groupe
 * (public), et une clé manquante partirait en MISSING_MESSAGE en production.
 */

const PHASES = {
    activite: 0,
    ca: 0,
    couple: 1,
    enfants: 1,
    caseT: 1,
    autres: 1,
    montantAutres: 1,
    situation: 2,
    resultat: 2,
};

const L = {
    fr: {
        phases: ["Votre activité", "Votre foyer", "Votre impôt"],
        retour: "Retour",
        continuer: "Continuer",
        recommencer: "Refaire la comparaison",

        qActivite: "Quelle est votre activité ?",
        qActiviteAide:
            "Elle fixe deux chiffres : la part de votre chiffre d'affaires que l'administration considère comme des frais et ne taxe pas (l'abattement), et le taux du versement libératoire, l'impôt à pourcentage fixe que vous pouvez choisir à la place du barème habituel.",
        vente: "Vente de marchandises",
        venteDetail: "Achat-revente, restauration, hôtellerie",
        services: "Prestations de services",
        servicesDetail:
            "Artisanat, bâtiment, coiffure, transport (catégorie BIC sur votre déclaration)",
        bnc: "Activité libérale",
        bncDetail:
            "Conseil, création, formation, professions réglementées (catégorie BNC sur votre déclaration)",

        qCa: "Combien encaissez-vous dans l'année ?",
        qCaAide:
            "Votre chiffre d'affaires, avant tout abattement. Vous pourrez le faire varier sur le résultat pour voir où la balance s'inverse.",
        ca: "Chiffre d'affaires encaissé dans l'année",
        caAide: "Déplacez le curseur : tout se recalcule.",
        explorerTitre: "Et si votre chiffre d'affaires changeait ?",

        qCouple: "Vivez-vous seul ou en couple ?",
        qCoupleAide:
            "Un couple marié ou pacsé déclare ensemble : deux parts au lieu d'une, donc un taux d'imposition plus bas sur le même revenu. Un concubin déclare seul.",
        seul: "Seul",
        seulDetail: "Célibataire, divorcé, veuf ou en concubinage",
        couple: "En couple",
        coupleDetail: "Marié ou pacsé, déclaration commune",

        qEnfants: "Combien d'enfants à charge ?",
        qEnfantsAide:
            "Chaque enfant ajoute une demi-part, une part entière à partir du troisième, ce qui abaisse l'impôt au barème. L'avantage est plafonné à 1 807 € par demi-part : au-delà, un enfant de plus ne fait plus baisser l'impôt, et nous en tenons compte.",
        enfants0: "Aucun",
        enfants1: "Un",
        enfants2: "Deux",
        enfants3: "Trois ou plus",
        enfants3Detail:
            "Au-delà de trois, nous comptons trois enfants : votre impôt au barème réel est un peu plus bas qu'affiché",

        qCaseT: "Élevez-vous seul vos enfants ?",
        qCaseTAide:
            "C'est la case T de votre déclaration. Elle change beaucoup plus que les autres : votre premier enfant vaut alors une part entière et non une demi-part, et l'avantage qu'il procure est plafonné à 4 262 € au lieu de 1 807 €. Sans elle, l'impôt au barème est surestimé et l'option paraît gagnante à tort.",
        caseTOui: "Oui, seul avec eux",
        caseTOuiDetail: "Personne d'autre ne vit à votre foyer, la case T est cochée",
        caseTNon: "Non",
        caseTNonDetail: "Vous vivez en concubinage, ou l'autre parent est au foyer",

        qAutres: "D'autres revenus entrent-ils dans votre foyer ?",
        qAutresAide:
            "Ils pèsent lourd dans le résultat. Un salaire ou une pension à côté fait monter votre foyer dans les tranches, et le versement libératoire devient vite gagnant. Sans autres revenus, tout dépend de votre chiffre d'affaires : le barème part de zéro alors que l'option prélève dès le premier euro. Le résultat vous dira de quel côté vous êtes.",
        autresNon: "Non, seulement la micro-entreprise",
        autresNonDetail: "Aucun autre revenu imposable dans le foyer",
        autresOui: "Oui, d'autres revenus",
        autresOuiDetail: "Salaire, pension, revenus fonciers, revenus du conjoint",
        qMontantAutres: "Combien, au total, sur l'année ?",
        qMontantAutresAide:
            "Le revenu net imposable de votre foyer hors micro-entreprise, celui de votre dernière déclaration : salaires nets imposables (la ligne « net imposable » de vos fiches de paie), pensions, revenus fonciers. Ajouté à votre bénéfice, il fixe la tranche, donc le taux appliqué.",
        autresRevenus: "Autres revenus imposables du foyer",

        qSituation: "Où en êtes-vous aujourd'hui ?",
        qSituationAide:
            "L'option se prend, ou s'abandonne, à des dates précises qui dépendent de votre situation. Le résultat vous dira ce que vous avez à faire, et avant quand.",
        creation: "Je n'ai pas encore créé",
        creationDetail: "Vous préparez votre déclaration de début d'activité",
        bareme: "Je suis au barème",
        baremeDetail: "C'est le cas par défaut si vous n'avez rien coché",
        vfl: "J'ai déjà opté pour le versement libératoire",
        vflDetail: "L'impôt est prélevé avec vos cotisations, à chaque déclaration",

        resultatTitre: "Barème ou versement libératoire",
        economieTitre: "Ce que l'option vous fait économiser, par an",
        economie: "soit {mois} par mois. Le versement libératoire est le bon choix pour vous.",
        surcoutTitre: "Ce que l'option vous coûte en plus, par an",
        surcout: "soit {mois} par mois. Le barème est le bon choix pour vous.",
        egalite:
            "Les deux reviennent au même dans votre situation : vous n'avez rien à gagner à opter, ni rien à perdre.",

        carteBareme: "Au barème, sans l'option",
        carteVfl: "Avec le versement libératoire",
        gagnant: "Le bon choix pour vous",
        beneficeLigne: "Bénéfice après abattement de {taux} : {montant}",
        revenuTotal: "Revenu imposable du foyer : {montant}",
        forfait: "Forfait de {taux} sur le chiffre d'affaires : {montant}",
        autresAuTaux: "Autres revenus, imposés au taux du foyer entier : {montant}",

        nonImposableTitre:
            "Votre foyer n'est pas imposable : sans l'option, votre impôt serait de zéro",
        nonImposableTexte:
            "Au barème, le revenu de votre foyer reste sous le seuil d'imposition et vous ne payez rien. Avec le versement libératoire, vous payez {taux} de tout ce que vous encaissez, soit {montant} sur l'année. C'est le cas le plus fréquent chez ceux qui optent « pour la simplicité » : le prélèvement paraît petit, mais il remplace un impôt de zéro.",

        plafondTitre: "Reste une condition de revenu",
        plafondTexte:
            "Avec un revenu imposable de {montant} pour {parts}, votre foyer dépasse probablement le plafond de {plafond} de revenu fiscal de référence au-delà duquel l'option est fermée. Ce test se fait sur votre avis d'impôt {anneeAvis}, celui de vos revenus {anneeRfr}, qui peut donner un autre chiffre que celui simulé ici.",
        plafondDejaOpteTitre: "Votre droit à l'option se vérifie chaque année",
        plafondDejaOpteTexte:
            "Avec un revenu imposable de {montant} pour {parts}, votre foyer dépasse probablement le plafond de {plafond} de revenu fiscal de référence fixé pour l'option. Si votre avis d'impôt {anneeAvis}, celui de vos revenus {anneeRfr}, le confirme, l'option cesse d'elle-même pour {annee} sans que vous ayez rien demandé : vérifiez-le chaque automne, à réception de votre avis.",
        plafondTroisEnfants:
            "Ce plafond est celui d'un foyer de trois enfants : il monte d'une part, soit {parPart}, par enfant supplémentaire.",
        unePart: "une part",
        nParts: "{n} parts",

        tauxEffectifTitre: "Pourquoi ce chiffre n'est pas celui des autres simulateurs",
        tauxEffectifTexte:
            "Avec l'option, votre chiffre d'affaires sort de la base imposable, mais il reste compté pour fixer le taux appliqué à vos autres revenus : c'est la règle du taux effectif, posée par l'article 197 C du code général des impôts pour que l'option ne fasse pas descendre vos salaires dans une tranche plus basse.",
        sansRegle:
            "Un simulateur qui l'ignore vous annoncerait {montant} avec l'option, soit {ecart} de moins que ce que vous paieriez réellement.",
        naifTrompe:
            "Il vous dirait même d'opter, à tort : avec la règle, l'option vous coûte {ecart} de plus que le barème.",
        naifMemeConclusion: "Ici, la règle change le montant, mais la conclusion reste la même.",

        basculeTitre: "À partir de quel taux l'option gagne",
        basculeTexte:
            "Votre taux moyen d'imposition, c'est l'impôt que paie votre foyer divisé par son revenu imposable, micro-entreprise comprise. Ne le confondez pas avec votre tranche. Dès qu'il dépasse {bascule}, l'option gagne. Ce seuil-là ne dépend ni de votre chiffre d'affaires ni de votre foyer, mais votre taux moyen, lui, en dépend : à revenus modestes, la décote, qui réduit les petits impôts, l'abaisse et repousse le chiffre d'affaires à partir duquel l'option devient gagnante. Avec vos chiffres, il est de {tauxMoyen}.",

        actionCreationOpter:
            "Vous n'avez pas encore créé : vous pourrez opter lors de votre déclaration de début d'activité, ou jusqu'au dernier jour du troisième mois qui la suit, et l'option s'appliquera dès vos premiers encaissements.",
        actionCreationRester:
            "Vous n'avez pas encore créé : ne cochez pas l'option lors de votre déclaration de début d'activité, le barème s'applique de lui-même.",
        actionBaremeOpter:
            "Vous êtes au barème : vous avez jusqu'au 30 septembre {annee} pour opter, soit {jours} jours, et l'option s'appliquera à vos encaissements de {anneeOption}.",
        actionBaremeOpterDernierJour:
            "Vous êtes au barème : c'est aujourd'hui, 30 septembre {annee}, le dernier jour pour opter pour {anneeOption}.",
        actionBaremeRester: "Vous êtes au barème : vous n'avez rien à faire.",
        actionVflGarder:
            "Vous avez déjà opté : vous n'avez rien à faire, l'option continue de s'appliquer.",
        actionVflRenoncer:
            "Vous avez déjà opté : renoncez à l'option avant le 30 septembre {annee}, soit {jours} jours, sinon elle continue de s'appliquer à vos encaissements de {anneeOption}.",
        actionVflRenoncerDernierJour:
            "Vous avez déjà opté : c'est aujourd'hui, 30 septembre {annee}, le dernier jour pour renoncer à l'option avant {anneeOption}.",

        eligibilite:
            "Le droit à l'option dépend aussi de votre revenu fiscal de référence : vérifiez-le avec ",
        eligibiliteDejaOpte:
            "Chaque année, votre revenu fiscal de référence doit rester sous le plafond : vérifiez-le avec ",
        eligibiliteLien: "le test d'éligibilité",

        note: "Ce calcul applique le barème 2026 sur les revenus 2025, le plafonnement du quotient familial et la décote, qui efface ou réduit les petits impôts. Il ne tient compte ni des réductions et crédits d'impôt, ni des situations particulières (parent isolé, invalidité, outre-mer). Il compare deux régimes d'imposition, il ne remplace pas votre avis d'impôt.",
        source: "Sources : barème et plafonnement du quotient familial sur service-public.gouv.fr, dates d'option sur legifrance.gouv.fr, décote et règle du taux effectif au Bulletin officiel des finances publiques.",
    },
    en: {
        phases: ["Your activity", "Your household", "Your tax"],
        retour: "Back",
        continuer: "Continue",
        recommencer: "Compare again",

        qActivite: "What is your activity?",
        qActiviteAide:
            "It sets two figures: the share of your turnover the tax office treats as expenses and does not tax (the allowance), and the rate of the versement libératoire, the flat-rate option you can choose instead of the standard progressive scale.",
        vente: "Sale of goods",
        venteDetail: "Retail, catering, hotel accommodation",
        services: "Services",
        servicesDetail: "Trades, building, hairdressing, transport (BIC category on your return)",
        bnc: "Liberal profession",
        bncDetail:
            "Consulting, creative work, training, regulated professions (BNC category on your return)",

        qCa: "How much do you collect over the year?",
        qCaAide:
            "Your turnover, before any allowance. You will be able to vary it on the result to see where the balance tips.",
        ca: "Turnover collected during the year",
        caAide: "Move the slider: everything recalculates.",
        explorerTitre: "What if your turnover changed?",

        qCouple: "Do you live alone or with a partner?",
        qCoupleAide:
            "A married or civil-partnered couple files together: two shares instead of one, so a lower tax rate on the same income. An unmarried partner files alone.",
        seul: "Alone",
        seulDetail: "Single, divorced, widowed or cohabiting",
        couple: "With a partner",
        coupleDetail: "Married or in a civil partnership, joint return",

        qEnfants: "How many dependent children?",
        qEnfantsAide:
            "Each child adds half a share, a full one from the third, which lowers the tax under the scale. The benefit is capped at 1,807 € per half share: beyond that, an extra child no longer lowers the tax, and we allow for it.",
        enfants0: "None",
        enfants1: "One",
        enfants2: "Two",
        enfants3: "Three or more",
        enfants3Detail:
            "Beyond three, we count three children: your real tax on the scale is slightly lower than shown",

        qCaseT: "Do you raise your children alone?",
        qCaseTAide:
            "This is box T on the French return. It changes far more than the others: your first child is then worth a full share rather than half a share, and the benefit it brings is capped at 4,262 € instead of 1,807 €. Without it, the tax on the scale is overstated and the option looks like a winner when it is not.",
        caseTOui: "Yes, alone with them",
        caseTOuiDetail: "Nobody else lives in your household, box T is ticked",
        caseTNon: "No",
        caseTNonDetail: "You live with a partner, or the other parent is in the household",

        qAutres: "Does any other income come into your household?",
        qAutresAide:
            "They weigh heavily on the outcome. A salary or a pension alongside pushes your household up the brackets, and the versement libératoire quickly wins. With no other income, it all depends on your turnover: the scale starts at zero while the option charges from the first euro. The result will tell you which side you are on.",
        autresNon: "No, only the micro-entreprise",
        autresNonDetail: "No other taxable income in the household",
        autresOui: "Yes, other income",
        autresOuiDetail: "Salary, pension, rental income, partner's income",
        qMontantAutres: "How much, in total, over the year?",
        qMontantAutresAide:
            "Your household's net taxable income excluding the micro-entreprise, as on your latest return: net taxable salaries (the figure on your payslips), pensions, rental income. Added to your profit, it sets the bracket, and therefore the rate applied.",
        autresRevenus: "Other taxable household income",

        qSituation: "Where do you stand today?",
        qSituationAide:
            "The option is taken up, or given up, on set dates that depend on your situation. The result will tell you what to do, and by when.",
        creation: "I have not registered yet",
        creationDetail: "You are preparing your business registration",
        bareme: "I am on the scale",
        baremeDetail: "That is the default if you never ticked the option",
        vfl: "I have already opted for the versement libératoire",
        vflDetail: "Tax is collected with your contributions, at each return",

        resultatTitre: "Scale or flat-rate option",
        economieTitre: "What the option saves you, per year",
        economie: "{mois} a month. The versement libératoire is the right choice for you.",
        surcoutTitre: "What the option costs you extra, per year",
        surcout: "{mois} a month. The scale is the right choice for you.",
        egalite:
            "Both come to the same thing in your situation: you have nothing to gain by opting in, and nothing to lose.",

        carteBareme: "On the scale, without the option",
        carteVfl: "With the flat-rate option (versement libératoire)",
        gagnant: "The right choice for you",
        beneficeLigne: "Profit after the {taux} allowance: {montant}",
        revenuTotal: "Household taxable income: {montant}",
        forfait: "{taux} flat rate on turnover: {montant}",
        autresAuTaux: "Other income, taxed at the whole household's rate: {montant}",

        nonImposableTitre:
            "Your household is not taxable: without the option, your tax would be zero",
        nonImposableTexte:
            "On the scale, your household income stays below the tax threshold and you pay nothing. With the flat-rate option, you pay {taux} of everything you collect, {montant} over the year. It is the most common case among those who opt in \"for simplicity\": the charge looks small, but it replaces a tax bill of zero.",

        plafondTitre: "An income condition still applies",
        plafondTexte:
            "With a taxable income of {montant} for {parts}, your household probably exceeds the {plafond} reference tax income cap that governs the option. That test is made on your {anneeAvis} tax notice, covering your {anneeRfr} income, which may give a different figure from the one simulated here.",
        plafondDejaOpteTitre: "Your right to the option is reassessed every year",
        plafondDejaOpteTexte:
            "With a taxable income of {montant} for {parts}, your household probably exceeds the {plafond} reference tax income cap that governs the option. If your {anneeAvis} tax notice, covering your {anneeRfr} income, confirms it, the option lapses by itself for {annee} without you asking for anything: check it every autumn, when your notice arrives.",
        plafondTroisEnfants:
            "That cap is the one for a household with three children: it rises by one share, {parPart}, for each additional child.",
        unePart: "one share",
        nParts: "{n} shares",

        tauxEffectifTitre: "Why this figure differs from other calculators",
        tauxEffectifTexte:
            "Under the option, your turnover leaves the taxable base but is still counted to set the rate applied to your other income: this is the effective rate rule, laid down by article 197 C of the French tax code so that the option cannot pull your salary down into a lower bracket.",
        sansRegle:
            "A calculator that ignores it would show you {montant} with the option, that is {ecart} less than what you would actually pay.",
        naifTrompe:
            "It would even tell you to opt in, wrongly: with the rule, the option costs you {ecart} more than the scale.",
        naifMemeConclusion: "Here, the rule changes the amount, but the conclusion stays the same.",

        basculeTitre: "The rate above which the option wins",
        basculeTexte:
            "Your average tax rate is the tax your household pays divided by its taxable income, micro-entreprise included. Do not confuse it with your bracket. As soon as it exceeds {bascule}, the option wins. That threshold depends neither on your turnover nor on your household, but your average rate does: on a modest income, the décote, the relief that trims small tax bills, lowers it and pushes back the turnover at which the option starts to win. With your figures, it is {tauxMoyen}.",

        actionCreationOpter:
            "You have not registered yet: you can opt in on your business registration, or until the last day of the third month after it, and the option will apply from your first receipts.",
        actionCreationRester:
            "You have not registered yet: do not tick the option on your business registration, the scale applies by itself.",
        actionBaremeOpter:
            "You are on the scale: you have until 30 September {annee} to opt in, {jours} days from now, and the option will apply to what you collect in {anneeOption}.",
        actionBaremeOpterDernierJour:
            "You are on the scale: today, 30 September {annee}, is the last day to opt in for {anneeOption}.",
        actionBaremeRester: "You are on the scale: there is nothing to do.",
        actionVflGarder:
            "You have already opted in: there is nothing to do, the option keeps applying.",
        actionVflRenoncer:
            "You have already opted in: give up the option before 30 September {annee}, {jours} days from now, otherwise it keeps applying to what you collect in {anneeOption}.",
        actionVflRenoncerDernierJour:
            "You have already opted in: today, 30 September {annee}, is the last day to give up the option before {anneeOption}.",

        eligibilite:
            "Your right to the option also depends on your reference tax income: check it with ",
        eligibiliteDejaOpte:
            "Each year, your reference tax income must stay below the cap: check it with ",
        eligibiliteLien: "the eligibility test",

        note: "This calculation applies the 2026 scale on 2025 income, the family quotient cap and the décote, the relief that wipes out or reduces small tax bills. It excludes tax reductions and credits, and special situations (single parent, disability, overseas territories). It compares two tax regimes; it does not replace your tax notice.",
        source: "Sources: scale and family quotient cap on service-public.gouv.fr, option deadlines on legifrance.gouv.fr, décote and effective rate rule in the BOFiP, the tax authority's official bulletin.",
    },
};

/**
 * Une des deux cartes du résultat : le régime, son impôt, et le détail qui
 * explique le montant. La carte gagnante passe au vert, l'autre reste neutre.
 */
function Carte({ titre, montant, lignes, gagnant, gagnantLibelle, lang, explication, ouvert, onBasculer }) {
    return (
        <div
            className={`rounded-xl p-5 border ${
                gagnant
                    ? "border-green-600/40 bg-green-600/[0.06]"
                    : "border-gray-200/70 dark:border-white/10"
            }`}
        >
            <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">{titre}</div>
            <div
                className={`text-3xl font-bold tabular-nums ${
                    gagnant ? "text-green-700 dark:text-green-400" : "dark:text-white"
                }`}
            >
                <MontantExplicable explication={explication} ouvert={ouvert} onBasculer={onBasculer}>
                    {euros(montant, lang)}
                </MontantExplicable>
            </div>
            {gagnant && (
                <div className="flex items-center gap-1.5 mt-2 text-xs font-semibold text-green-700 dark:text-green-400">
                    <SolarIcon name="check-circle-linear" className="w-4 h-4 shrink-0" />
                    {gagnantLibelle}
                </div>
            )}
            <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-3 space-y-1">
                {lignes.map((ligne) => (
                    <div key={ligne}>{ligne}</div>
                ))}
            </div>
            {ouvert && <PanneauExplication explication={explication} lang={lang} />}
        </div>
    );
}

/**
 * Un bloc d'alerte ou d'explication du résultat : une icône, un titre, et le
 * texte que l'appelant y met. Ambre pour ce qui bloque, primaire pour ce qui
 * éclaire.
 */
function Encart({ ton, icone, titre, children }) {
    const ambre = ton === "ambre";
    return (
        <div
            className={`mt-5 rounded-xl border p-5 ${
                ambre
                    ? "border-amber-500/40 bg-amber-500/[0.06]"
                    : "border-primary/40 bg-primary/[0.06]"
            }`}
        >
            <div className="flex items-start gap-2.5">
                <SolarIcon
                    name={icone}
                    className={`w-6 h-6 shrink-0 mt-0.5 ${
                        ambre ? "text-amber-600 dark:text-amber-400" : "text-primary"
                    }`}
                />
                <div className="font-bold dark:text-white leading-snug">{titre}</div>
            </div>
            {children}
        </div>
    );
}

/**
 * La marche à suivre, selon la situation du lecteur et le gagnant.
 *
 * Trois situations et deux issues font six phrases, et aucune ne se déduit
 * d'une autre : qui n'a pas créé opte à la création sans compter de jours,
 * qui est au barème a une date limite pour opter, qui a déjà opté a la même
 * date limite pour y renoncer. Rien n'est dit en cas d'égalité.
 */
function marcheASuivre({ t, situation, gagnant, echeance }) {
    if (gagnant === "egalite") return null;
    const opter = gagnant === "vfl";

    if (situation === "creation") {
        return opter ? t.actionCreationOpter : t.actionCreationRester;
    }
    if (situation === "bareme" && !opter) return t.actionBaremeRester;
    if (situation === "vfl" && opter) return t.actionVflGarder;
    if (!echeance) return null;

    const dernierJour = echeance.jours === 1;
    const texte =
        situation === "bareme"
            ? dernierJour
                ? t.actionBaremeOpterDernierJour
                : t.actionBaremeOpter
            : dernierJour
              ? t.actionVflRenoncerDernierJour
              : t.actionVflRenoncer;
    return remplir(texte, {
        annee: echeance.annee,
        anneeOption: echeance.anneeOption,
        jours: echeance.jours,
    });
}

/**
 * Résultat chiffré.
 *
 * Le compteur vit ici, dans le composant monté à l'arrivée sur le résultat,
 * et non dans le parcours : lancé plus tôt, il aurait fini de compter avant
 * que quiconque le regarde. Seuls les gros chiffres montent ; les phrases
 * gardent leurs valeurs finales, sinon elles tremblent pendant la montée.
 *
 * L'ordre est celui de la question posée : d'abord la réponse (l'écart en
 * gros), puis les deux montants avec le gagnant en premier, puis le curseur
 * pour explorer, puis les explications.
 */
function ResultatChiffre({
    t,
    lang,
    r,
    naif,
    echeance,
    activite,
    situation,
    autresRevenus,
    ca,
    setCa,
    onRetour,
    onRecommencer,
    sens,
    reduit,
}) {
    const p = useMontants(reduit);
    const { vfl, abattement } = ACTIVITES[activite];

    // Chaque montant du résultat dit d'où il vient : ses étapes et ses sources.
    const ex = useMemo(
        () =>
            expliquerVersementLiberatoire(
                {
                    chiffreAffaires: ca,
                    activite,
                    autresRevenus,
                    enCouple: r.enCouple,
                    enfants: r.enfants,
                    parentIsole: r.parentIsole,
                },
                lang,
            ),
        [ca, activite, autresRevenus, r.enCouple, r.enfants, r.parentIsole, lang],
    );
    const { ouverte, basculer } = useExplications();

    // Le foyer non imposable est le cas pédagogique de l'outil : au barème il
    // ne paie rien, et l'option prélèverait quand même. On le nomme plutôt que
    // d'afficher un simple perdant.
    const nonImposable = r.gagnant === "bareme" && r.impotBareme < 1;

    // Ceux pour qui l'option gagne le plus sont ceux qui risquent de ne pas y
    // avoir droit. Le revenu simulé tient lieu de revenu fiscal de référence :
    // c'est une approximation (le vrai test porte sur l'avis d'impôt de
    // l'année précédente), et la phrase le dit.
    const droit =
        r.gagnant === "vfl"
            ? eligible({
                  revenuFiscalReference: r.revenuTotal,
                  enCouple: r.enCouple,
                  enfants: r.enfants,
                  parentIsole: r.parentIsole,
                  annee: echeance ? echeance.anneeOption : ANNEE_OPTION_PAR_DEFAUT,
              })
            : null;
    // `eligible` rend `null` pour une année dont le plafond n'est pas encore
    // publié : sans barème, on ne peut annoncer ni droit ni dépassement.
    const depassePlafond = droit ? droit.eligible === false : false;
    // Qui a déjà opté vient de lire « vous n'avez rien à faire » : l'encart ne
    // lui parle pas d'un droit à obtenir mais d'un droit qui se rejoue chaque
    // année et qu'il peut perdre sans l'avoir demandé.
    const dejaOpte = situation === "vfl";

    const lignesBareme = [
        remplir(t.beneficeLigne, {
            taux: pourcent(abattement, lang),
            montant: euros(r.benefice, lang),
        }),
        remplir(t.revenuTotal, { montant: euros(r.revenuTotal, lang) }),
    ];
    const lignesVfl = [
        remplir(t.forfait, { taux: pourcent(vfl, lang), montant: euros(r.vflSurCa, lang) }),
    ];
    if (autresRevenus > 0) {
        lignesVfl.push(
            remplir(t.autresAuTaux, { montant: euros(r.impotAutresRevenusEnVfl, lang) }),
        );
    }

    const cartes = [
        {
            cle: "bareme",
            titre: t.carteBareme,
            montant: r.impotBareme * p,
            lignes: lignesBareme,
            gagnant: r.gagnant === "bareme",
        },
        {
            cle: "vfl",
            titre: t.carteVfl,
            montant: r.impotVfl * p,
            lignes: lignesVfl,
            gagnant: r.gagnant === "vfl",
        },
    ];
    // Le gagnant en premier : sur mobile, les cartes s'empilent et le premier
    // gros chiffre lu doit être celui du bon choix.
    if (r.gagnant === "vfl") cartes.reverse();

    const action = marcheASuivre({ t, situation, gagnant: r.gagnant, echeance });

    const partsFoyer = parts({ enCouple: r.enCouple, enfants: r.enfants, parentIsole: r.parentIsole });

    return (
        <Resultat onRetour={onRetour} retour={t.retour} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {nonImposable ? (
                <Encart ton="ambre" icone="danger-triangle-linear" titre={t.nonImposableTitre}>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                        {remplir(t.nonImposableTexte, {
                            taux: pourcent(vfl, lang),
                            montant: euros(r.vflSurCa, lang),
                        })}
                    </p>
                </Encart>
            ) : r.gagnant === "egalite" ? (
                <div className="mt-4 rounded-xl p-5 border border-gray-200/70 dark:border-white/10">
                    <p className="text-base font-medium text-gray-600 dark:text-lightBlue mb-0">
                        {t.egalite}
                    </p>
                </div>
            ) : (
                <div
                    className={`mt-4 rounded-xl p-5 border ${
                        r.gagnant === "vfl"
                            ? "border-green-600/40 bg-green-600/[0.06]"
                            : "border-amber-500/40 bg-amber-500/[0.06]"
                    }`}
                >
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">
                        {r.gagnant === "vfl" ? t.economieTitre : t.surcoutTitre}
                    </div>
                    <div
                        className={`text-3xl font-bold tabular-nums ${
                            r.gagnant === "vfl"
                                ? "text-green-700 dark:text-green-400"
                                : "text-amber-600 dark:text-amber-400"
                        }`}
                    >
                        <MontantExplicable explication={ex.ecart} ouvert={ouverte === "ecart"} onBasculer={() => basculer("ecart")}>
                            {euros(Math.abs(r.ecart) * p, lang)}
                        </MontantExplicable>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-1 mb-0">
                        {remplir(r.gagnant === "vfl" ? t.economie : t.surcout, {
                            mois: euros(Math.abs(r.ecart) / 12, lang),
                        })}
                    </p>
                    {ouverte === "ecart" && <PanneauExplication explication={ex.ecart} lang={lang} />}
                </div>
            )}

            <IndiceExplication lang={lang} />

            {depassePlafond && (
                <Encart
                    ton="ambre"
                    icone="danger-triangle-linear"
                    titre={dejaOpte ? t.plafondDejaOpteTitre : t.plafondTitre}
                >
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                        {remplir(dejaOpte ? t.plafondDejaOpteTexte : t.plafondTexte, {
                            montant: euros(r.revenuTotal, lang),
                            parts:
                                partsFoyer === 1
                                    ? t.unePart
                                    : remplir(t.nParts, {
                                          n: partsFoyer.toLocaleString(
                                              lang === "fr" ? "fr-FR" : "en-US",
                                          ),
                                      }),
                            plafond: euros(droit.plafond, lang),
                            anneeAvis: droit.anneeAvis,
                            anneeRfr: droit.anneeRfr,
                            annee: droit.annee,
                        })}
                        {r.enfants >= 3 && (
                            <>
                                {" "}
                                {remplir(t.plafondTroisEnfants, {
                                    parPart: euros(droit.plafondParPart, lang),
                                })}
                            </>
                        )}
                    </p>
                </Encart>
            )}

            <div className="grid sm:grid-cols-2 gap-3 mt-5">
                {cartes.map((carte) => (
                    <Carte
                        key={carte.cle}
                        titre={carte.titre}
                        montant={carte.montant}
                        lignes={carte.lignes}
                        gagnant={carte.gagnant}
                        gagnantLibelle={t.gagnant}
                        lang={lang}
                        explication={ex[carte.cle]}
                        ouvert={ouverte === carte.cle}
                        onBasculer={() => basculer(carte.cle)}
                    />
                ))}
            </div>

            <div className="mt-6">
                <div className="text-sm font-semibold dark:text-white mb-3">{t.explorerTitre}</div>
                <Curseur
                    libelle={t.ca}
                    aide={t.caAide}
                    valeur={ca}
                    poser={setCa}
                    maximum={PLAFONDS[activite]}
                    pas={activite === "vente" ? 1000 : 500}
                    formater={(v) => euros(v, lang)}
                />
            </div>

            {naif && naif.ecart >= 1 && (
                <Encart ton="primaire" icone="lightbulb-linear" titre={t.tauxEffectifTitre}>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-2">
                        {t.tauxEffectifTexte}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mb-0">
                        {remplir(t.sansRegle, {
                            montant: euros(naif.montant, lang),
                            ecart: euros(naif.ecart, lang),
                        })}{" "}
                        {naif.montant < r.impotBareme && r.gagnant === "bareme"
                            ? remplir(t.naifTrompe, { ecart: euros(Math.abs(r.ecart), lang) })
                            : t.naifMemeConclusion}
                    </p>
                </Encart>
            )}

            <div className="mt-6 pt-5 border-t border-gray-200/70 dark:border-white/10">
                <div className="text-sm font-semibold dark:text-white">{t.basculeTitre}</div>
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-1.5 mb-0">
                    {remplir(t.basculeTexte, {
                        bascule: pourcent(r.bascule, lang),
                        tauxMoyen: pourcent(r.tauxMoyen, lang),
                    })}
                </p>
                {action && (
                    <p className="text-sm font-medium text-gray-700 dark:text-white mt-3 mb-0">
                        {action}
                    </p>
                )}
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                    {dejaOpte ? t.eligibiliteDejaOpte : t.eligibilite}
                    <Link
                        href={{
                            pathname: "/tools/[slug]",
                            params: {
                                slug:
                                    lang === "fr"
                                        ? "eligibilite-versement-liberatoire"
                                        : "flat-rate-income-tax-eligibility",
                            },
                        }}
                        className="text-primary underline underline-offset-2"
                    >
                        {t.eligibiliteLien}
                    </Link>
                    .
                </p>
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-1">{t.note}</p>
            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mb-0">{t.source}</p>

            <Recommencer onClick={onRecommencer} libelle={t.recommencer} />
        </Resultat>
    );
}

export default function SimulateurVersementLiberatoire({ lang = "fr", aujourdhui = null }) {
    const t = L[lang] || L.fr;

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    const { valeurs: dossier } = useDossierOutil();
    // Comme au choix de l'activité, un chiffre d'affaires au-dessus du plafond
    // est ramené au plafond : le curseur le tairait et le calcul le garderait.
    // Il se redemande alors (écran « ca » non sauté).
    const caDansPlafond = dossier.ca !== undefined && dossier.activite !== undefined && dossier.ca <= PLAFONDS[dossier.activite];
    const [activite, setActivite] = useState(dossier.activite ?? null);
    const [enCouple, setEnCouple] = useState(dossier.enCouple ?? false);
    const [enfants, setEnfants] = useState(dossier.enfants ?? 0);
    const [parentIsole, setParentIsole] = useState(false);
    const [autresRevenus, setAutresRevenus] = useState(dossier.autresRevenus ?? 0);
    const [situation, setSituation] = useState(dossier.situation ?? "bareme");
    const [ca, setCa] = useState(
        dossier.ca === undefined
            ? 36000
            : dossier.activite !== undefined
              ? Math.min(dossier.ca, PLAFONDS[dossier.activite])
              : dossier.ca,
    );

    const { etape, sens, reduit, aller, retour, recommencer, communs } = useParcours(
        "activite",
        () => {
            setActivite(null);
            setEnCouple(false);
            setEnfants(0);
            setParentIsole(false);
            setAutresRevenus(0);
            setSituation("bareme");
            setCa(36000);
        },
        PHASES,
        reprendre([
            { etape: "activite", connu: dossier.activite !== undefined },
            { etape: "ca", connu: caDansPlafond },
            { etape: "couple", connu: dossier.enCouple !== undefined },
            { etape: "enfants", connu: dossier.enfants !== undefined },
            // La case T ne se déduit pas du dossier : le parcours s'y arrête.
            ...(!dossier.enCouple && dossier.enfants >= 1 ? [{ etape: "caseT", connu: false }] : []),
            { etape: "autres", connu: dossier.autresRevenus !== undefined },
            ...(dossier.autresRevenus > 0 ? [{ etape: "montantAutres", connu: true }] : []),
            { etape: "situation", connu: dossier.situation !== undefined },
            { etape: "resultat" },
        ]),
    );

    useSignalerDossier({ activite, enCouple, enfants, autresRevenus, situation, ca }, etape === "resultat");

    const r = useMemo(
        () =>
            activite
                ? {
                      ...comparerVersementLiberatoire({
                          chiffreAffaires: ca,
                          activite,
                          autresRevenus,
                          enCouple,
                          enfants,
                          parentIsole,
                      }),
                      enCouple,
                      enfants,
                      parentIsole,
                  }
                : null,
        [ca, activite, autresRevenus, enCouple, enfants, parentIsole],
    );

    // Ce que donnerait le calcul sans la règle du taux effectif : le forfait,
    // plus l'impôt des autres revenus calculés comme si le chiffre d'affaires
    // n'existait pas. On montre l'écart plutôt que de l'affirmer.
    const naif = useMemo(() => {
        if (!r || autresRevenus <= 0) return null;
        const sansRegle =
            r.vflSurCa +
            comparerVersementLiberatoire({
                chiffreAffaires: 0,
                activite,
                autresRevenus,
                enCouple,
                enfants,
                parentIsole,
            }).impotBareme;
        return { montant: sansRegle, ecart: r.impotVfl - sansRegle };
    }, [r, activite, autresRevenus, enCouple, enfants, parentIsole]);

    // La date vient de la page plutôt que d'un new Date() ici : lue des deux
    // côtés, elle produirait un écart d'hydratation sur le compte à rebours.
    // L'option se prend, et se dénonce, au plus tard le 30 septembre de
    // l'année qui précède ; passée cette date, c'est l'année suivante qui
    // s'ouvre. Le jour est compté en UTC par la lib : entre minuit et deux
    // heures du matin, heure de Paris, le 1er octobre, le résultat annonce
    // encore le 30 septembre. Deux heures par an, assumées.
    const echeance = useMemo(() => {
        if (!aujourdhui) return null;
        const date = new Date(aujourdhui);
        let annee = ANNEE_OPTION_PAR_DEFAUT;
        let jours = joursAvantEcheance(annee, date);
        if (jours <= 0) {
            annee += 1;
            jours = joursAvantEcheance(annee, date);
        }
        return { anneeOption: annee, annee: annee - 1, jours };
    }, [aujourdhui]);

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };

    if (etape === "activite") {
        return (
            <Question
                key={etape}
                {...communs}
                phases={t.phases}
                retour={t.retour}
                phase={PHASES.activite}
                titre={t.qActivite}
                aide={t.qActiviteAide}
                choix={["vente", "services", "bnc"].map((cle) => ({
                    icone: {
                        vente: "sale-linear",
                        services: "pen-2-linear",
                        bnc: "brain-linear",
                    }[cle],
                    titre: t[cle],
                    detail: t[`${cle}Detail`],
                    onClick: () => {
                        setActivite(cle);
                        // Un retour depuis une autre activité peut laisser un
                        // chiffre d'affaires au-dessus du plafond de celle-ci :
                        // le curseur le tairait et le calcul le garderait.
                        setCa((c) => Math.min(c, PLAFONDS[cle]));
                        aller("ca");
                    },
                }))}
            />
        );
    }

    if (etape === "ca") {
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.ca}
                titre={t.qCa}
                aide={t.qCaAide}
            >
                <Curseur
                    libelle={t.ca}
                    valeur={ca}
                    poser={setCa}
                    maximum={PLAFONDS[activite]}
                    pas={activite === "vente" ? 1000 : 500}
                    formater={(v) => euros(v, lang)}
                />
                <Avancer onClick={() => aller("couple")}>{t.continuer}</Avancer>
            </Ecran>
        );
    }

    if (etape === "couple") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.couple}
                titre={t.qCouple}
                aide={t.qCoupleAide}
                choix={[
                    {
                        icone: "user-rounded-linear",
                        titre: t.seul,
                        detail: t.seulDetail,
                        onClick: () => {
                            setEnCouple(false);
                            aller("enfants");
                        },
                    },
                    {
                        icone: "users-group-rounded-linear",
                        titre: t.couple,
                        detail: t.coupleDetail,
                        onClick: () => {
                            setEnCouple(true);
                            aller("enfants");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "enfants") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.enfants}
                titre={t.qEnfants}
                aide={t.qEnfantsAide}
                choix={[0, 1, 2, 3].map((n) => ({
                    icone: [
                        "close-circle-linear",
                        "user-plus-linear",
                        "users-group-two-rounded-linear",
                        "users-group-rounded-linear",
                    ][n],
                    titre: t[`enfants${n}`],
                    detail: t[`enfants${n}Detail`],
                    onClick: () => {
                        setEnfants(n);
                        // La case T n'existe que pour une personne seule ayant
                        // au moins un enfant : ne pas poser la question aux
                        // autres, qui n'auraient rien à en faire.
                        if (!enCouple && n >= 1) {
                            aller("caseT");
                            return;
                        }
                        setParentIsole(false);
                        aller("autres");
                    },
                }))}
            />
        );
    }

    if (etape === "caseT") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.caseT}
                titre={t.qCaseT}
                aide={t.qCaseTAide}
                choix={[
                    {
                        icone: "user-hand-up-linear",
                        titre: t.caseTOui,
                        detail: t.caseTOuiDetail,
                        onClick: () => {
                            setParentIsole(true);
                            aller("autres");
                        },
                    },
                    {
                        icone: "users-group-rounded-linear",
                        titre: t.caseTNon,
                        detail: t.caseTNonDetail,
                        onClick: () => {
                            setParentIsole(false);
                            aller("autres");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "autres") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.autres}
                titre={t.qAutres}
                aide={t.qAutresAide}
                choix={[
                    {
                        icone: "close-circle-linear",
                        titre: t.autresNon,
                        detail: t.autresNonDetail,
                        onClick: () => {
                            setAutresRevenus(0);
                            // Sans autres revenus, il n'y a pas de montant à
                            // demander : on passe directement à la situation.
                            aller("situation");
                        },
                    },
                    {
                        icone: "wallet-money-linear",
                        titre: t.autresOui,
                        detail: t.autresOuiDetail,
                        onClick: () => {
                            setAutresRevenus(30000);
                            aller("montantAutres");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "montantAutres") {
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.montantAutres}
                titre={t.qMontantAutres}
                aide={t.qMontantAutresAide}
            >
                <Curseur
                    libelle={t.autresRevenus}
                    valeur={autresRevenus}
                    poser={setAutresRevenus}
                    maximum={120000}
                    pas={1000}
                    formater={(v) => euros(v, lang)}
                />
                <Avancer onClick={() => aller("situation")}>{t.continuer}</Avancer>
            </Ecran>
        );
    }

    if (etape === "situation") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.situation}
                titre={t.qSituation}
                aide={t.qSituationAide}
                choix={["creation", "bareme", "vfl"].map((cle) => ({
                    icone: {
                        creation: "rocket-2-linear",
                        bareme: "bill-list-linear",
                        vfl: "check-circle-linear",
                    }[cle],
                    titre: t[cle],
                    detail: t[`${cle}Detail`],
                    onClick: () => {
                        setSituation(cle);
                        aller("resultat");
                    },
                }))}
            />
        );
    }

    return (
        <ResultatChiffre
            key={etape}
            t={t}
            lang={lang}
            r={r}
            naif={naif}
            echeance={echeance}
            activite={activite}
            situation={situation}
            autresRevenus={autresRevenus}
            ca={ca}
            setCa={setCa}
            onRetour={retour}
            onRecommencer={recommencer}
            sens={sens}
            reduit={reduit}
        />
    );
}
